import {
  CATEGORIES,
  getConsent,
  hasDecision,
  setConsent
} from "./consent.js";

const VISIBLE_CLASS = "cookiebar--visible";
// Anchors are the only thing the CMS writer field can produce, so a link to
// this fragment reopens the panel too. That is how the privacy policy points
// at it without needing a custom block.
const SETTINGS_HASH = "#cookie-settings";
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function cookiebar() {
  const bar = document.querySelector(".cookiebar");

  if (!bar) {
    return;
  }

  const views = [].slice.call(bar.querySelectorAll("[data-cookiebar-view]"));
  const checkboxes = [].slice.call(
    bar.querySelectorAll("[data-cookiebar-category]")
  );
  const actions = [].slice.call(
    bar.querySelectorAll("[data-cookiebar-action]")
  );
  const disclosures = [].slice.call(
    bar.querySelectorAll("[data-cookiebar-disclosure]")
  );

  // Only set while the panel was opened deliberately from a settings link, so
  // we know where to send focus back to and whether to trap it.
  let triggerElement = null;

  function isOpen() {
    return bar.classList.contains(VISIBLE_CLASS);
  }

  function showView(name) {
    views.forEach(view => {
      view.hidden = view.dataset.cookiebarView !== name;
    });
  }

  // Reflects the stored decision (or all-off for a first-time visitor) into
  // the checkboxes, so reopening the panel shows the current state.
  function syncCheckboxes() {
    const consent = getConsent();

    checkboxes.forEach(checkbox => {
      const category = checkbox.dataset.cookiebarCategory;

      checkbox.checked = Boolean(consent && consent[category]);
    });
  }

  function readCheckboxes() {
    const selection = {};

    CATEGORIES.forEach(category => {
      selection[category] = false;
    });

    checkboxes.forEach(checkbox => {
      selection[checkbox.dataset.cookiebarCategory] = checkbox.checked;
    });

    return selection;
  }

  function setDisclosure(button, isExpanded) {
    const details = document.getElementById(
      button.getAttribute("aria-controls")
    );
    const label = button.querySelector(".cookiebar__disclosure-label");

    button.setAttribute("aria-expanded", isExpanded ? "true" : "false");

    if (details) {
      details.hidden = !isExpanded;
    }

    if (label) {
      label.textContent = isExpanded
        ? button.dataset.hideText
        : button.dataset.showText;
    }
  }

  function collapseAllDetails() {
    disclosures.forEach(button => {
      setDisclosure(button, false);
    });
  }

  function close() {
    bar.classList.remove(VISIBLE_CLASS);

    if (triggerElement) {
      triggerElement.focus();
      triggerElement = null;
    }
  }

  // `trigger` is the element to send focus back to on close, if there is one.
  function open(trigger) {
    syncCheckboxes();
    collapseAllDetails();

    // Always start on the intro, however the panel was opened. The detailed
    // view is one click away via Customise.
    showView("intro");
    bar.classList.add(VISIBLE_CLASS);

    if (trigger) {
      triggerElement = trigger;
    }

    // Focus the panel itself (tabindex="-1") rather than the first button, so
    // a screen reader announces the dialog and its description before the
    // choices. This happens on every open, including the unprompted one on a
    // first visit: it is a decision that gates the page, so leaving focus back
    // on <body> means keyboard users have to hunt for it.
    bar.focus();
  }

  function decide(selection) {
    setConsent(selection);
    close();
  }

  function selectionWhere(value) {
    const selection = {};

    CATEGORIES.forEach(category => {
      selection[category] = value;
    });

    return selection;
  }

  actions.forEach(button => {
    button.addEventListener("click", () => {
      const action = button.dataset.cookiebarAction;

      if (action === "accept-all") {
        decide(selectionWhere(true));
      } else if (action === "reject-all") {
        decide(selectionWhere(false));
      } else if (action === "save") {
        decide(readCheckboxes());
      } else if (action === "customise") {
        showView("customise");
        bar.focus();
      }
    });
  });

  disclosures.forEach(button => {
    button.addEventListener("click", () => {
      const wasExpanded = button.getAttribute("aria-expanded") === "true";

      // Accordion: only one category's details stay open. With three of them
      // expanded at once the panel outgrows the viewport and starts scrolling,
      // which buries the accept and decline buttons.
      collapseAllDetails();

      if (!wasExpanded) {
        setDisclosure(button, true);
      }
    });
  });

  bar.addEventListener("keydown", event => {
    if (event.key === "Escape") {
      // Closing without choosing must not be read as agreement. Nothing is
      // stored, so the banner comes back on the next page load.
      close();
      return;
    }

    // Trap Tab inside the panel only when it was opened as a real modal from
    // a settings link.
    if (event.key !== "Tab" || !triggerElement) {
      return;
    }

    const focusable = [].slice
      .call(bar.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter(element => element.offsetParent !== null);

    if (focusable.length === 0) {
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  // Any element anywhere can reopen the panel — the footer link, a button on
  // the privacy page, or the notice inside a blocked video.
  function findTrigger(target) {
    if (!target || typeof target.closest !== "function") {
      return null;
    }

    const explicit = target.closest("[data-cookie-settings]");

    if (explicit) {
      return explicit;
    }

    const link = target.closest('a[href*="' + SETTINGS_HASH + '"]');

    return link && link.hash === SETTINGS_HASH ? link : null;
  }

  document.addEventListener("click", event => {
    const trigger = findTrigger(event.target);

    if (!trigger) {
      return;
    }

    event.preventDefault();

    if (isOpen() && triggerElement) {
      close();
    } else {
      open(trigger);
    }
  });

  syncCheckboxes();

  if (window.location.hash === SETTINGS_HASH) {
    // Someone followed a link to the settings from another page.
    open(null);
  } else if (!hasDecision()) {
    open(null);
  }
}
